import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';

import { TFunction } from 'i18next';
import { TrapType } from 'components/AnalyticsPage/TrapType';
import { HeatMapChartData } from 'api/traps/types';

export const heatMapChartOptions = (
  chartData: HeatMapChartData,
  type: TrapType,
  setSelectedMonth: (month: string) => void,
  selectedMonth: string | null,
  currentYear: string,
  setChartHeight: (height: number) => void,
  t: TFunction,
  showTooltip?: boolean,
  customTooltip?: (options: any) => any,
): ApexOptions => {
  const chartOptions: ApexOptions = {
    series: chartData.seriesList,
    chart: {
      type: 'heatmap',
      selection: { enabled: false },
      toolbar: {
        tools: {
          selection: false,
          zoom: false,
          zoomin: true,
          zoomout: true,
        },
      },
      events: {
        dataPointSelection: function (event, chartContext, config) {
          if (selectedMonth === null) {
            setChartHeight(600);
            setSelectedMonth(String(config.seriesIndex + 1));
          }
        },
      },
    },
    plotOptions: {
      heatmap: {
        enableShades: false,
        radius: 1,
        useFillColorAsStroke: true,
        colorScale: {
          ranges: chartData.ranges,
        },
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (_val: number, opts: any) {
        if (_val === -1000000) return '';
        const dataPoint = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];

        const capture = dataPoint.captureCount ?? 0;
        const nonCapture = dataPoint.nonCaptureCount ?? 0;

        if (capture === 0 && nonCapture === 0) return 0;
        if (capture > 0 && nonCapture === 0) return capture;
        if (capture === 0 && nonCapture > 0) return nonCapture;

        return `${capture}/${nonCapture}`;
      },
    },
    xaxis: {
      type: 'category',
      range: 23,
      labels: {
        show: true,
        trim: false,
      },
      tickPlacement: 'between',
      tickAmount: 'dataPoints',
      title: {
        text: `${selectedMonth ? t('MCTDetail.heatmap.hour_of_day') : t('MCTDetail.heatmap.day_of_month')}`,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      title: {
        text: `${selectedMonth ? t('MCTDetail.heatmap.day_of_month') : t('MCTDetail.heatmap.month_of_year')}`,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
      labels: {
        show: true,
        formatter: (value: number) => {
          if (selectedMonth) {
            const dayName =
              dayjs(`${currentYear}-${selectedMonth}-${value}`).format('ddd') === 'Mon'
                ? t('MCTDetail.heatmap.mon')
                : '';
            return `${dayName} ${value}`;
          }
          return value.toString();
        },
      },
    },
    stroke: {
      width: 0.2,
    },
    title: {
      text: `${type === TrapType.SRST ? t('MCTDetail.heatmap.titleRat') : type === TrapType.SRBM ? t('MCTDetail.heatmap.titleSRBM') : t('MCTDetail.heatmap.title')} - ${
        selectedMonth
          ? `${dayjs()
              .month(Number(selectedMonth) - 1)
              .format('MMMM')} ${currentYear}`
          : currentYear
      }`,
      style: {
        fontWeight: 'bold',
        fontFamily: '"Poppins", sans-serif',
      },
    },
    tooltip: {
      enabled: showTooltip,
      custom: customTooltip,
    },
    legend: {
      show: true,
      height: 60,
      customLegendItems: [
        `<div class="heatmap-legend">
    <div class="legend-group">
      <div class="legend-title">${t('MCTDetail.heatmap.titleUpcoming')}</div>
      <div class="legend-items">
        <div class="legend-item">
          <span class="legend-color" style="background-color: #9563FF;"></span>
          <span class="legend-label"></span>
        </div>
      </div>
    </div>

    <div class="legend-group">
      <div class="legend-title">${t('MCTDetail.heatmap.titleNoEvents')}</div>
      <div class="legend-items">
        <div class="legend-item">
          <span class="legend-color" style="background-color: #8BC34A;"></span>
          <span class="legend-label">0</span>
        </div>
      </div>
    </div>

    <div class="legend-group">
      <div class="legend-title">${type === TrapType.SRBM ? t('MCTDetail.heatmap.titleHeatMapSRBMConsumption') : t('MCTDetail.heatmap.titleHeatMapTrapCaptures')}</div>
      <div class="legend-items">
        <div class="legend-item">
          <span class="legend-color" style="background-color: #FDE0E0;"></span>
          <span class="legend-label">1</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #F9B4B4;"></span>
          <span class="legend-label">2</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #F27A7A;"></span>
          <span class="legend-label">3-5</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #E94B4B;"></span>
          <span class="legend-label">6-10</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #C62828;"></span>
          <span class="legend-label">11-20</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #7F1D1D;"></span>
          <span class="legend-label">20+</span>
        </div>
      </div>
    </div>

    <div class="legend-group">
      <div class="legend-title">${type === TrapType.SRBM ? t('MCTDetail.heatmap.titleHeatMapSRBMActivity') : t('MCTDetail.heatmap.titleHeatMapTrapNonCaptures')}</div>
      <div class="legend-items">
        <div class="legend-item">
          <span class="legend-color" style="background-color: #FFE6C9;"></span>
          <span class="legend-label">1</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #FFC98B;"></span>
          <span class="legend-label">2</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #F4A24F;"></span>
          <span class="legend-label">3-4</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background-color: #E07A1F;"></span>
          <span class="legend-label">5+</span>
        </div>
      </div>
    </div>
  </div>`,
      ],
      markers: {
        customHTML: () => {
          return '';
        },
      },
    },
  };

  return chartOptions;
};
